// src/constants/defaultRoles.js
// Predefined role definitions with permission mappings

import { PERMISSIONS } from "./permissions.js";

// ============================================================================
// DEFAULT ROLE DEFINITIONS
// ============================================================================

/**
 * Predefined roles for the union management system
 * These roles are global (tenantId = null) and cannot be modified or deleted
 *
 * Each role contains:
 * - name: Display name of the role
 * - description: What this role is for
 * - permissions: Object mapping permission keys to boolean values
 */

const DEFAULT_ROLES = [
  // ==========================================================================
  // LEADERSHIP ROLES
  // ==========================================================================

  {
    name: "President",
    description: "Union President - Full administrative access to all features",
    permissions: {
      // Grievances
      [PERMISSIONS.VIEW_ALL_GRIEVANCES]: true,
      [PERMISSIONS.MANAGE_GRIEVANCES]: true,
      [PERMISSIONS.VIEW_CONFIDENTIAL_GRIEVANCES]: true,
      [PERMISSIONS.VIEW_GRIEVANCE_WORKFLOWS]: true,
      [PERMISSIONS.MANAGE_GRIEVANCE_WORKFLOWS]: true,
      [PERMISSIONS.FILE_GRIEVANCE_FOR_ANY_DEPARTMENT]: true,

      // Communications
      [PERMISSIONS.SEND_BULK_EMAIL]: true,
      [PERMISSIONS.SEND_BULK_SMS]: true,
      [PERMISSIONS.SEND_PUSH_NOTIFICATIONS]: true,
      [PERMISSIONS.CREATE_POSTS]: true,
      [PERMISSIONS.MANAGE_POSTS]: true,
      [PERMISSIONS.PIN_POSTS]: true,

      // Documents
      [PERMISSIONS.UPLOAD_DOCUMENTS]: true,
      [PERMISSIONS.MANAGE_DOCUMENTS]: true,
      [PERMISSIONS.DELETE_ANY_DOCUMENT]: true,

      // Events
      [PERMISSIONS.CREATE_EVENTS]: true,
      [PERMISSIONS.MANAGE_EVENTS]: true,
      [PERMISSIONS.DELETE_ANY_EVENT]: true,

      // Hall Reservations
      [PERMISSIONS.APPROVE_HALL_RESERVATIONS]: true,
      [PERMISSIONS.MANAGE_HALL_RESERVATIONS]: true,

      // Admin
      [PERMISSIONS.VIEW_ALL_USERS]: true,
      [PERMISSIONS.MANAGE_USERS]: true,
      [PERMISSIONS.MANAGE_USER_NOTES]: true,
      [PERMISSIONS.MANAGE_ROLES]: true,
      [PERMISSIONS.MANAGE_DEPARTMENTS]: true,
      [PERMISSIONS.VIEW_AUDIT_LOGS]: true,
      [PERMISSIONS.VIEW_ANALYTICS]: true,

      // Leadership
      [PERMISSIONS.MANAGE_LEADERSHIP]: true,

      // Financial
      [PERMISSIONS.VIEW_FINANCIALS]: true,
      [PERMISSIONS.MANAGE_FINANCIALS]: true,
      [PERMISSIONS.APPROVE_EXPENSES]: true,

      // Voting
      [PERMISSIONS.CREATE_VOTES]: true,
      [PERMISSIONS.MANAGE_VOTES]: true,

      // Store
      [PERMISSIONS.MANAGE_STORE]: true,
      [PERMISSIONS.VIEW_STORE_ORDERS]: true,

      // Bargaining
      [PERMISSIONS.VIEW_BARGAINING_NOTES]: true,
      [PERMISSIONS.MANAGE_BARGAINING_NOTES]: true,

      // Task Management
      [PERMISSIONS.VIEW_ALL_TASKS]: true,
      [PERMISSIONS.MANAGE_TASKS]: true,
      [PERMISSIONS.ASSIGN_TASKS]: true,
      [PERMISSIONS.VIEW_TASK_ANALYTICS]: true,

      // System
      [PERMISSIONS.MANAGE_SYSTEM_SETTINGS]: true,
    },
  },

  {
    name: "Vice President",
    description:
      "Union Vice President - Broad access to union operations and management",
    permissions: {
      // Grievances
      [PERMISSIONS.VIEW_ALL_GRIEVANCES]: true,
      [PERMISSIONS.MANAGE_GRIEVANCES]: true,
      [PERMISSIONS.VIEW_CONFIDENTIAL_GRIEVANCES]: true,
      [PERMISSIONS.VIEW_GRIEVANCE_WORKFLOWS]: true,
      [PERMISSIONS.MANAGE_GRIEVANCE_WORKFLOWS]: true,
      [PERMISSIONS.FILE_GRIEVANCE_FOR_ANY_DEPARTMENT]: true,

      // Communications
      [PERMISSIONS.SEND_BULK_EMAIL]: true,
      [PERMISSIONS.SEND_BULK_SMS]: true,
      [PERMISSIONS.SEND_PUSH_NOTIFICATIONS]: true,
      [PERMISSIONS.CREATE_POSTS]: true,
      [PERMISSIONS.MANAGE_POSTS]: true,
      [PERMISSIONS.PIN_POSTS]: true,

      // Documents
      [PERMISSIONS.UPLOAD_DOCUMENTS]: true,
      [PERMISSIONS.MANAGE_DOCUMENTS]: true,

      // Events
      [PERMISSIONS.CREATE_EVENTS]: true,
      [PERMISSIONS.MANAGE_EVENTS]: true,

      // Hall Reservations
      [PERMISSIONS.APPROVE_HALL_RESERVATIONS]: true,

      // Admin
      [PERMISSIONS.VIEW_ALL_USERS]: true,
      [PERMISSIONS.MANAGE_USER_NOTES]: true,
      [PERMISSIONS.VIEW_ANALYTICS]: true,

      // Voting
      [PERMISSIONS.CREATE_VOTES]: true,
      [PERMISSIONS.MANAGE_VOTES]: true,

      // Task Management
      [PERMISSIONS.VIEW_ALL_TASKS]: true,
      [PERMISSIONS.MANAGE_TASKS]: true,
      [PERMISSIONS.ASSIGN_TASKS]: true,
      [PERMISSIONS.VIEW_TASK_ANALYTICS]: true,
    },
  },

  {
    name: "Secretary",
    description:
      "Union Secretary - Manages communications, documents, and events",
    permissions: {
      // Grievances
      [PERMISSIONS.FILE_GRIEVANCE_FOR_ANY_DEPARTMENT]: true,

      // Communications
      [PERMISSIONS.SEND_BULK_EMAIL]: true,
      [PERMISSIONS.CREATE_POSTS]: true,
      [PERMISSIONS.MANAGE_POSTS]: true,
      [PERMISSIONS.PIN_POSTS]: true,

      // Documents
      [PERMISSIONS.UPLOAD_DOCUMENTS]: true,
      [PERMISSIONS.MANAGE_DOCUMENTS]: true,

      // Events
      [PERMISSIONS.CREATE_EVENTS]: true,
      [PERMISSIONS.MANAGE_EVENTS]: true,

      // Hall Reservations
      [PERMISSIONS.APPROVE_HALL_RESERVATIONS]: true,

      // Admin
      [PERMISSIONS.VIEW_ALL_USERS]: true,

      // Task Management
      [PERMISSIONS.VIEW_ALL_TASKS]: true,
      [PERMISSIONS.MANAGE_TASKS]: true,
      [PERMISSIONS.ASSIGN_TASKS]: true,
    },
  },

  {
    name: "Treasurer",
    description: "Union Treasurer - Manages financial data and reporting",
    permissions: {
      // Admin
      [PERMISSIONS.VIEW_ANALYTICS]: true,

      // Financial
      [PERMISSIONS.VIEW_FINANCIALS]: true,
      [PERMISSIONS.MANAGE_FINANCIALS]: true,
      [PERMISSIONS.APPROVE_EXPENSES]: true,

      // Communications
      [PERMISSIONS.CREATE_POSTS]: true,
    },
  },

  {
    name: "Trustee",
    description: "Union Trustee - Oversees operations and reviews audit logs",
    permissions: {
      // Admin
      [PERMISSIONS.VIEW_AUDIT_LOGS]: true,
      [PERMISSIONS.VIEW_ANALYTICS]: true,

      // Financial
      [PERMISSIONS.VIEW_FINANCIALS]: true,

      // Communications
      [PERMISSIONS.CREATE_POSTS]: true,

      // Task Management
      [PERMISSIONS.VIEW_ALL_TASKS]: true,
      [PERMISSIONS.VIEW_TASK_ANALYTICS]: true,
    },
  },

  {
    name: "EBoard Rep",
    description:
      "Executive Board Representative - Access to grievances and event management",
    permissions: {
      // Grievances
      [PERMISSIONS.VIEW_ALL_GRIEVANCES]: true,

      // Communications
      [PERMISSIONS.CREATE_POSTS]: true,

      // Events
      [PERMISSIONS.CREATE_EVENTS]: true,

      // Admin
      [PERMISSIONS.VIEW_ALL_USERS]: true,

      // Task Management
      [PERMISSIONS.VIEW_ALL_TASKS]: true,
    },
  },

  // ==========================================================================
  // FUNCTIONAL ROLES
  // ==========================================================================

  {
    name: "Grievance Committee Member",
    description:
      "Grievance Committee - Full access to view and manage all member grievances",
    permissions: {
      // Grievances
      [PERMISSIONS.VIEW_ALL_GRIEVANCES]: true,
      [PERMISSIONS.MANAGE_GRIEVANCES]: true,
      [PERMISSIONS.VIEW_CONFIDENTIAL_GRIEVANCES]: true,

      // Communications
      [PERMISSIONS.CREATE_POSTS]: true,
    },
  },

  {
    name: "Steward",
    description:
      "Union Steward - Handles member grievances and workplace issues",
    permissions: {
      // Grievances
      [PERMISSIONS.VIEW_ALL_GRIEVANCES]: true,
      [PERMISSIONS.MANAGE_GRIEVANCES]: true,
      [PERMISSIONS.VIEW_CONFIDENTIAL_GRIEVANCES]: true,

      // Communications
      [PERMISSIONS.CREATE_POSTS]: true,

      // Admin
      [PERMISSIONS.VIEW_ALL_USERS]: true,

      // Task Management
      [PERMISSIONS.VIEW_ALL_TASKS]: true,
    },
  },

  {
    name: "Safety Committee Member",
    description:
      "Safety Committee - Manages safety-related communications and documents",
    permissions: {
      // Communications
      [PERMISSIONS.CREATE_POSTS]: true,

      // Documents
      [PERMISSIONS.UPLOAD_DOCUMENTS]: true,

      // Events
      [PERMISSIONS.CREATE_EVENTS]: true,

      // Task Management
      [PERMISSIONS.MANAGE_TASKS]: true,
      [PERMISSIONS.ASSIGN_TASKS]: true,
    },
  },

  {
    name: "Political Action Committee Member",
    description:
      "Political Action Committee - Manages political events and communications",
    permissions: {
      // Communications
      [PERMISSIONS.CREATE_POSTS]: true,
      [PERMISSIONS.SEND_BULK_EMAIL]: true,

      // Events
      [PERMISSIONS.CREATE_EVENTS]: true,
      [PERMISSIONS.MANAGE_EVENTS]: true,

      // Documents
      [PERMISSIONS.UPLOAD_DOCUMENTS]: true,

      // Task Management
      [PERMISSIONS.MANAGE_TASKS]: true,
      [PERMISSIONS.ASSIGN_TASKS]: true,
    },
  },

  {
    name: "Membership Committee Member",
    description:
      "Membership Committee - Manages member communications and basic user management",
    permissions: {
      // Communications
      [PERMISSIONS.SEND_BULK_EMAIL]: true,
      [PERMISSIONS.CREATE_POSTS]: true,

      // Admin
      [PERMISSIONS.VIEW_ALL_USERS]: true,

      // Events
      [PERMISSIONS.CREATE_EVENTS]: true,

      // Task Management
      [PERMISSIONS.MANAGE_TASKS]: true,
      [PERMISSIONS.ASSIGN_TASKS]: true,
    },
  },

  {
    name: "Bargaining Team Member",
    description:
      "Bargaining Team - Access to contract documents and bargaining notes",
    permissions: {
      // Documents
      [PERMISSIONS.UPLOAD_DOCUMENTS]: true,
      [PERMISSIONS.MANAGE_DOCUMENTS]: true,

      // Bargaining
      [PERMISSIONS.VIEW_BARGAINING_NOTES]: true,
      [PERMISSIONS.MANAGE_BARGAINING_NOTES]: true,

      // Communications
      [PERMISSIONS.CREATE_POSTS]: true,

      // Task Management
      [PERMISSIONS.MANAGE_TASKS]: true,
      [PERMISSIONS.ASSIGN_TASKS]: true,
    },
  },

  // ==========================================================================
  // ADMINISTRATIVE ROLES
  // ==========================================================================

  {
    name: "Local Admin",
    description:
      "Local Administrator - Full tenant-level administrative access (excludes platform management)",
    permissions: {
      // Grievances
      [PERMISSIONS.VIEW_ALL_GRIEVANCES]: true,
      [PERMISSIONS.MANAGE_GRIEVANCES]: true,
      [PERMISSIONS.VIEW_CONFIDENTIAL_GRIEVANCES]: true,
      [PERMISSIONS.VIEW_GRIEVANCE_WORKFLOWS]: true,
      [PERMISSIONS.MANAGE_GRIEVANCE_WORKFLOWS]: true,

      // Communications
      [PERMISSIONS.SEND_BULK_EMAIL]: true,
      [PERMISSIONS.SEND_BULK_SMS]: true,
      [PERMISSIONS.SEND_PUSH_NOTIFICATIONS]: true,
      [PERMISSIONS.CREATE_POSTS]: true,
      [PERMISSIONS.MANAGE_POSTS]: true,
      [PERMISSIONS.PIN_POSTS]: true,

      // Documents
      [PERMISSIONS.UPLOAD_DOCUMENTS]: true,
      [PERMISSIONS.MANAGE_DOCUMENTS]: true,
      [PERMISSIONS.DELETE_ANY_DOCUMENT]: true,

      // Events
      [PERMISSIONS.CREATE_EVENTS]: true,
      [PERMISSIONS.MANAGE_EVENTS]: true,
      [PERMISSIONS.DELETE_ANY_EVENT]: true,

      // Hall Reservations
      [PERMISSIONS.APPROVE_HALL_RESERVATIONS]: true,
      [PERMISSIONS.MANAGE_HALL_RESERVATIONS]: true,

      // Admin
      [PERMISSIONS.VIEW_ALL_USERS]: true,
      [PERMISSIONS.MANAGE_USERS]: true,
      [PERMISSIONS.MANAGE_USER_NOTES]: true,
      [PERMISSIONS.MANAGE_ROLES]: true,
      [PERMISSIONS.MANAGE_DEPARTMENTS]: true,
      [PERMISSIONS.VIEW_AUDIT_LOGS]: true,
      [PERMISSIONS.VIEW_ANALYTICS]: true,

      // Leadership
      [PERMISSIONS.MANAGE_LEADERSHIP]: true,

      // Financial
      [PERMISSIONS.VIEW_FINANCIALS]: true,
      [PERMISSIONS.MANAGE_FINANCIALS]: true,
      [PERMISSIONS.APPROVE_EXPENSES]: true,

      // Voting
      [PERMISSIONS.CREATE_VOTES]: true,
      [PERMISSIONS.MANAGE_VOTES]: true,

      // Store
      [PERMISSIONS.MANAGE_STORE]: true,
      [PERMISSIONS.VIEW_STORE_ORDERS]: true,

      // Bargaining
      [PERMISSIONS.VIEW_BARGAINING_NOTES]: true,
      [PERMISSIONS.MANAGE_BARGAINING_NOTES]: true,

      // Task Management
      [PERMISSIONS.VIEW_ALL_TASKS]: true,
      [PERMISSIONS.MANAGE_TASKS]: true,
      [PERMISSIONS.ASSIGN_TASKS]: true,
      [PERMISSIONS.VIEW_TASK_ANALYTICS]: true,

      // System (tenant-level only, not platform-level)
      [PERMISSIONS.MANAGE_SYSTEM_SETTINGS]: true,
    },
  },

  {
    name: "Super Admin",
    description:
      "Platform Super Administrator - Full access to all features across all tenants",
    permissions: {
      // ALL PERMISSIONS
      [PERMISSIONS.VIEW_ALL_GRIEVANCES]: true,
      [PERMISSIONS.MANAGE_GRIEVANCES]: true,
      [PERMISSIONS.VIEW_CONFIDENTIAL_GRIEVANCES]: true,
      [PERMISSIONS.SEND_BULK_EMAIL]: true,
      [PERMISSIONS.SEND_BULK_SMS]: true,
      [PERMISSIONS.SEND_PUSH_NOTIFICATIONS]: true,
      [PERMISSIONS.CREATE_POSTS]: true,
      [PERMISSIONS.MANAGE_POSTS]: true,
      [PERMISSIONS.PIN_POSTS]: true,
      [PERMISSIONS.UPLOAD_DOCUMENTS]: true,
      [PERMISSIONS.MANAGE_DOCUMENTS]: true,
      [PERMISSIONS.DELETE_ANY_DOCUMENT]: true,
      [PERMISSIONS.CREATE_EVENTS]: true,
      [PERMISSIONS.MANAGE_EVENTS]: true,
      [PERMISSIONS.DELETE_ANY_EVENT]: true,
      [PERMISSIONS.APPROVE_HALL_RESERVATIONS]: true,
      [PERMISSIONS.MANAGE_HALL_RESERVATIONS]: true,
      [PERMISSIONS.VIEW_ALL_USERS]: true,
      [PERMISSIONS.MANAGE_USERS]: true,
      [PERMISSIONS.MANAGE_USER_NOTES]: true,
      [PERMISSIONS.MANAGE_ROLES]: true,
      [PERMISSIONS.MANAGE_DEPARTMENTS]: true,
      [PERMISSIONS.VIEW_AUDIT_LOGS]: true,
      [PERMISSIONS.VIEW_ANALYTICS]: true,
      [PERMISSIONS.MANAGE_LEADERSHIP]: true,
      [PERMISSIONS.VIEW_FINANCIALS]: true,
      [PERMISSIONS.MANAGE_FINANCIALS]: true,
      [PERMISSIONS.APPROVE_EXPENSES]: true,
      [PERMISSIONS.CREATE_VOTES]: true,
      [PERMISSIONS.MANAGE_VOTES]: true,
      [PERMISSIONS.MANAGE_STORE]: true,
      [PERMISSIONS.VIEW_STORE_ORDERS]: true,
      [PERMISSIONS.VIEW_BARGAINING_NOTES]: true,
      [PERMISSIONS.MANAGE_BARGAINING_NOTES]: true,
      [PERMISSIONS.VIEW_ALL_TASKS]: true,
      [PERMISSIONS.MANAGE_TASKS]: true,
      [PERMISSIONS.ASSIGN_TASKS]: true,
      [PERMISSIONS.VIEW_TASK_ANALYTICS]: true,
      [PERMISSIONS.MANAGE_SYSTEM_SETTINGS]: true,

      // Platform-level permissions
      [PERMISSIONS.MANAGE_TENANTS]: true,
    },
  },

  {
    name: "General Member",
    description:
      "Default role for all union members - Basic access to member features",
    permissions: {
      // General members have no special permissions
      // They can:
      // - View their own grievances (handled by ownership check, not permission)
      // - View documents (default access)
      // - View events and RSVP (default access)
      // - Request hall reservations (default access)
      // - View their own profile and limited directory
      // - Vote on polls (when implemented)
      // No explicit permissions needed - basic functionality is default
    },
  },
];

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get a default role by name
 * @param {string} roleName - Name of the role to retrieve
 * @returns {Object|null} Role object or null if not found
 */
function getDefaultRole(roleName) {
  return DEFAULT_ROLES.find((role) => role.name === roleName) || null;
}

/**
 * Get all default role names
 * @returns {Array<string>} Array of role names
 */
function getDefaultRoleNames() {
  return DEFAULT_ROLES.map((role) => role.name);
}

/**
 * Check if a role name is a predefined role
 * @param {string} roleName - Name to check
 * @returns {boolean} True if role is predefined
 */
function isPredefinedRole(roleName) {
  return DEFAULT_ROLES.some((role) => role.name === roleName);
}

// ============================================================================
// EXPORTS
// ============================================================================

export { DEFAULT_ROLES, getDefaultRole, getDefaultRoleNames, isPredefinedRole };
